import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Wind, Menu, X, ArrowRight, FileText } from 'lucide-react';
import axios from 'axios';
import logger from '@/utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AirParticles({ count = 15, color = 'rgba(63,167,214,0.15)' }) {
  const particles = useMemo(() => Array.from({ length: count }, (_, i) => ({
    id: i, left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
    size: 4 + Math.random() * 14, delay: Math.random() * 10,
    duration: 10 + Math.random() * 15, dx: -100 + Math.random() * 300, dy: -200 + Math.random() * 100,
  })), [count]);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(p => (
        <div key={p.id} className="air-particle" style={{
          left: p.left, top: p.top, width: p.size, height: p.size,
          background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
          animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s`,
          '--dx': `${p.dx}px`, '--dy': `${p.dy}px`,
        }} />
      ))}
    </div>
  );
}

export function DemoModal({ isOpen, onClose }) {
  const [form, setForm] = useState({ name: '', email: '', company: '', city: 'Mumbai', requirement: '', booking_type: 'demo' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  if (!isOpen) return null;
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await axios.post(`${API}/demo-booking`, form); setSubmitted(true); }
    catch (err) { logger.error('Demo booking failed:', err.message); }
    setLoading(false);
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="demo-modal-overlay">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />
      <div className="relative rounded-3xl p-8 md:p-10 max-w-lg w-full shadow-2xl" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-dark)' }} data-testid="demo-modal">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full transition-colors hover:bg-white/5" data-testid="close-demo-modal">
          <X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
        </button>
        {submitted ? (
          <div className="text-center py-8" data-testid="demo-success">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(74,222,128,0.1)' }}>
              <Wind className="w-7 h-7" style={{ color: 'var(--success)' }} />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Inter' }}>Booked Successfully</h3>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Our team will reach out within 24 hours.</p>
            <button onClick={onClose} className="btn-glow mt-6 text-sm px-6 py-3" data-testid="demo-done-btn">Done</button>
          </div>
        ) : (
          <>
            <p className="overline-glow mb-2">Book a Demo</p>
            <h3 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Inter' }}>Experience Vayu</h3>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="demo-form">
              <input data-testid="demo-name" type="text" required placeholder="Full Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-3 rounded-xl text-sm" style={{ border: '1px solid var(--border-dark)', background: 'var(--bg-deep)', color: 'var(--text-light)' }} />
              <input data-testid="demo-email" type="email" required placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3 rounded-xl text-sm" style={{ border: '1px solid var(--border-dark)', background: 'var(--bg-deep)', color: 'var(--text-light)' }} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="demo-company" type="text" placeholder="Company" value={form.company} onChange={e => setForm({...form, company: e.target.value})} className="w-full px-4 py-3 rounded-xl text-sm" style={{ border: '1px solid var(--border-dark)', background: 'var(--bg-deep)', color: 'var(--text-light)' }} />
                <select data-testid="demo-city" value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="w-full px-4 py-3 rounded-xl text-sm" style={{ border: '1px solid var(--border-dark)', background: 'var(--bg-deep)', color: 'var(--text-light)' }}>
                  {['Mumbai','Delhi','Bangalore','Chennai','Kolkata','Hyderabad','Pune','Ahmedabad','Other'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <textarea data-testid="demo-requirement" placeholder="Tell us about your requirement..." rows={3} value={form.requirement} onChange={e => setForm({...form, requirement: e.target.value})} className="w-full px-4 py-3 rounded-xl text-sm resize-none" style={{ border: '1px solid var(--border-dark)', background: 'var(--bg-deep)', color: 'var(--text-light)' }} />
              <button type="submit" disabled={loading} className="btn-glow w-full" data-testid="demo-submit" style={{ opacity: loading ? 0.7 : 1 }}>{loading ? 'Booking...' : 'Book Demo'}</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export function Navbar({ onBookDemo }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const navLinks = [
    { label: 'Product', to: '/product' },
    { label: 'Technology', to: '/technology' },
    { label: 'Builders', to: '/builders' },
    { label: 'About', to: '/about' },
  ];
  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'glass-dark' : 'bg-transparent'}`} data-testid="main-navbar">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-20 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
          <Wind className="w-5 h-5" style={{ color: 'var(--aqua)' }} strokeWidth={1.5} />
          <span className="text-base font-semibold tracking-tight" style={{ fontFamily: 'Inter', color: 'var(--text-light)' }}>vayu</span>
          <span className="text-[9px] font-medium opacity-40 ml-0.5" style={{ color: 'var(--text-secondary-light)', fontFamily: 'Inter' }}>by Jagdale Air</span>
        </Link>
        <div className="hidden lg:flex items-center gap-7">
          {navLinks.map(l => (
            <Link key={l.to} to={l.to} className="text-[13px] font-medium transition-all duration-300 hover:text-cyan-400" style={{ color: 'rgba(230,244,247,0.75)', fontFamily: 'DM Sans' }} data-testid={`nav-${l.label.toLowerCase()}`}>{l.label}</Link>
          ))}
          <Link to="/investor" className="text-[13px] font-medium flex items-center gap-1.5 transition-all duration-300 hover:text-cyan-400" style={{ color: 'rgba(230,244,247,0.75)', fontFamily: 'DM Sans' }} data-testid="nav-investor">
            <FileText className="w-3.5 h-3.5" /> Investor Deck
          </Link>
          {user ? (
            <Link to="/dashboard" className="btn-glow text-[12px] py-2 px-5" data-testid="nav-dashboard">Dashboard</Link>
          ) : (
            <button onClick={onBookDemo} className="btn-glow text-[12px] py-2 px-5 flex items-center gap-1.5" data-testid="nav-book-demo">Book a Demo <ArrowRight className="w-3 h-3" /></button>
          )}
        </div>
        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X className="w-5 h-5" style={{ color: 'var(--text-light)' }} /> : <Menu className="w-5 h-5" style={{ color: 'var(--text-light)' }} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden glass-dark px-6 pb-6 pt-2" data-testid="mobile-menu">
          {navLinks.map(l => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-3 text-sm font-medium" style={{ color: 'var(--text-secondary-light)', borderBottom: '1px solid var(--border-dark)' }}>{l.label}</Link>
          ))}
          <Link to="/investor" onClick={() => setOpen(false)} className="block py-3 text-sm font-medium flex items-center gap-2" style={{ color: 'var(--text-secondary-light)', borderBottom: '1px solid var(--border-dark)' }}><FileText className="w-3.5 h-3.5" /> Investor Deck</Link>
          <Link to="/contact" onClick={() => setOpen(false)} className="block py-3 text-sm font-medium" style={{ color: 'var(--text-secondary-light)' }}>Contact</Link>
          <button onClick={() => { onBookDemo(); setOpen(false); }} className="btn-glow w-full mt-4 text-sm" data-testid="mobile-book-demo">Book a Demo</button>
        </div>
      )}
    </nav>
  );
}

export function Footer() {
  return (
    <footer className="relative overflow-hidden" style={{ background: '#030D10' }} data-testid="footer">
      <AirParticles count={8} color="rgba(63,167,214,0.04)" />
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-20 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <Wind className="w-5 h-5" style={{ color: 'var(--aqua)' }} strokeWidth={1.5} />
              <span className="text-base font-semibold tracking-tight" style={{ fontFamily: 'Inter', color: 'var(--text-light)' }}>vayu</span>
              <span className="text-[9px] font-medium opacity-30 ml-0.5" style={{ color: 'var(--text-secondary-light)' }}>by Jagdale Air</span>
            </div>
            <p className="text-sm leading-relaxed max-w-sm" style={{ color: 'var(--text-muted)' }}>
              Building the operating system for air in buildings. Because the air you breathe defines how you live.
            </p>
          </div>
          <div>
            <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] mb-4" style={{ color: 'var(--text-secondary-light)' }}>Explore</h4>
            <div className="space-y-2.5">
              {[{l:'Product',t:'/product'},{l:'Technology',t:'/technology'},{l:'Builders',t:'/builders'},{l:'About',t:'/about'}].map(({l,t}) => (
                <Link key={t} to={t} className="block text-sm transition-colors hover:text-white" style={{ color: 'var(--text-muted)' }}>{l}</Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] mb-4" style={{ color: 'var(--text-secondary-light)' }}>Connect</h4>
            <div className="space-y-2.5">
              <Link to="/contact" className="block text-sm transition-colors hover:text-white" style={{ color: 'var(--text-muted)' }}>Contact Us</Link>
              <Link to="/investor" className="block text-sm transition-colors hover:text-white" style={{ color: 'var(--text-muted)' }}>Investors</Link>
              <a href="mailto:info@jagdaleair.com" className="block text-sm transition-colors hover:text-white" style={{ color: 'var(--text-muted)' }}>info@jagdaleair.com</a>
            </div>
          </div>
        </div>
        <div className="pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>&copy; {new Date().getFullYear()} Jagdale Air Pvt. Ltd. All rights reserved.</p>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>Vayu — Smart Air Infrastructure | jagdaleair.com</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
